import { buildClusterFaqJsonLdAsync } from "@/lib/clusterFaq";
import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import { ServicePageEnhancer } from "@/components/service/ServicePageEnhancer";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import AnswerBoxTop from "@/components/AnswerBoxTop";
import { HubCountryLinks } from "@/components/HubCountryLinks";
// /mfa-legalisation — TH hub for MFA + Embassy legalisation across non-Apostille countries (Phase P-MFA-LEGALISATION)
import { createFileRoute, Link } from "@tanstack/react-router";
import { MFA_COUNTRIES } from "@/data/mfa-legalisation-countries";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { Stamp, Globe2, Phone, MessageCircle, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/mfa-legalisation/")({
  loader: async () => ({ clusterFaq: await buildClusterFaqJsonLdAsync("mfa", "th", "https://nyclanguageinstitute.com/mfa-legalisation") }),
  head: ({ loaderData }: any) => {
    const thPath = "/mfa-legalisation";
    const enPath = "/en/mfa-legalisation";
    const title = `รับรองเอกสาร MFA + สถานทูต ${MFA_COUNTRIES.length}+ ประเทศ (Non-Apostille) | NYC`;
    const desc = `บริการรับรองเอกสารกรมการกงสุล (กระทรวงการต่างประเทศ) + สถานทูตต่างชาติในไทย — สำหรับประเทศที่ไม่อยู่ใน Hague Apostille (จีน, UAE, ซาอุฯ, เวียดนาม, ไต้หวัน ฯลฯ). แปล ${MFA_COUNTRIES.length} ภาษา, รับเอกสารถึงบ้าน, ครบจบ 5-14 วัน. ปรึกษาก่อนยื่นจริง ลดโอกาสเอกสารตีกลับ`;
    const url = `${SITE}${thPath}`;
    return {
      meta: [
        { title: ctaTitle(title, "/mfa-legalisation", "th") }, { name: "description", content: ctaDesc(desc, "/mfa-legalisation", "th")},
        { property: "og:title", content: ctaTitle(title, "/mfa-legalisation", "th")}, { property: "og:description", content: ctaDesc(desc, "/mfa-legalisation", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
        { name: "keywords", content: "MFA, รับรองเอกสารกระทรวงต่างประเทศ, embassy legalisation, รับรองสถานทูต, non-apostille, แจ้งวัฒนะ, กรมการกงสุล, attestation thailand" },
      ],
      links: canonicalAndHreflang(thPath, enPath),
      scripts: [
        {
          type: "application/ld+json" as const,
          children: JSON.stringify({
            "@context": "https://schema.org", "@type": "CollectionPage", "@id": `${url}#collection`,
            name: title, description: desc, url,
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: MFA_COUNTRIES.length,
              itemListElement: MFA_COUNTRIES.map((c, i) => ({
                "@type": "ListItem", position: i + 1, name: `รับรองเอกสารสำหรับ${c.nameTh}`,
                url: `${SITE}/mfa-legalisation/${c.slug}`,
              })),
            },
          }),
        },
        {
          type: "application/ld+json" as const,
          children: JSON.stringify({
            "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
            name: "MFA + Embassy Consular Legalisation Thailand",
            serviceType: "MFA + Embassy Consular Legalisation Thailand",
            provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
            areaServed: "TH",
            offers: { "@type": "Offer", price: "3000", priceCurrency: "THB", description: "MFA legalisation per document" },
          }),
        },
        {
          type: "application/ld+json" as const,
          children: JSON.stringify({
            "@context": "https://schema.org", "@type": "FAQPage", "@id": `${url}#faq`,
            mainEntity: [
              { "@type": "Question", name: "MFA Legalisation ต่างจาก Apostille อย่างไร?",
                acceptedAnswer: { "@type": "Answer", text: "Apostille ใช้ระหว่างประเทศที่เซ็นอนุสัญญา Hague — รับรองชั้นเดียวที่ MFA. แต่สำหรับประเทศที่ไม่ใช่สมาชิก (จีน, UAE, ซาอุฯ, เวียดนาม, ไต้หวัน) ต้องรับรอง MFA ไทย + สถานทูตของประเทศปลายทางในกรุงเทพฯ เป็น 2 ชั้น." } },
              { "@type": "Question", name: "ใช้เวลานานเท่าไหร่?",
                acceptedAnswer: { "@type": "Answer", text: "เริ่มต้น 5 วัน (ลาว, กัมพูชา) ถึง 14 วัน (ซาอุฯ, ปากีสถาน, อิหร่าน, อียิปต์) ขึ้นอยู่กับขั้นตอนของสถานทูตและภาษาที่ต้องแปล. NYC ดูแลคิวสถานทูตและรายงาน milestone ทุกขั้น." } },
              { "@type": "Question", name: "ค่าธรรมเนียมรวมประมาณเท่าไหร่?",
                acceptedAnswer: { "@type": "Answer", text: "MFA 200 บาท/ฉบับ + ค่าสถานทูต 800–3,200 บาท/ฉบับขึ้นกับประเทศ + แปลเริ่มต้น 800 บาท/หน้า. NYC คิดเหมาเริ่มต้น 3,000 บาท/ฉบับ รวมค่ารับส่งและคิวด่วน." } },
              { "@type": "Question", name: "เอกสารแปลต้องใช้ภาษาอะไร?",
                acceptedAnswer: { "@type": "Answer", text: "ส่วนใหญ่อังกฤษ. ซาอุฯ/คูเวต/อิหร่าน/อียิปต์ บังคับอาหรับหรือฟาร์ซี. ไต้หวัน/จีน บังคับจีน. NYC มีนักแปลขึ้นทะเบียนสถานทูตทุกภาษา." } },
            ],
          }),
        },
      
        ...(loaderData?.clusterFaq ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.clusterFaq) }] : []),
      ],
    };
  },
  component: MfaHubTh,
});

function MfaHubTh() {
  const byRegion = MFA_COUNTRIES.reduce<Record<string, typeof MFA_COUNTRIES>>((acc, c) => {
    (acc[c.region] ||= []).push(c); return acc;
  }, {});
  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-6xl">
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Stamp className="mr-1 inline h-3 w-3" /> Non-Apostille · MFA + Embassy
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            รับรองเอกสารกระทรวงต่างประเทศ + สถานทูต {MFA_COUNTRIES.length}+ ประเทศ
          </h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            สำหรับประเทศปลายทางที่ <strong>ไม่ใช่</strong>สมาชิก Hague Apostille — จีน, UAE, ซาอุฯ, กาตาร์, คูเวต, เวียดนาม, ไต้หวัน, อิหร่าน, อียิปต์ ฯลฯ.
            NYC ดูแลครบ <strong>แปล + Notary + MFA แจ้งวัฒนะ + สถานทูต</strong> ภายใน 5–14 วัน
          </p>
          <div className="quick-answer mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">เอกสารส่งประเทศที่ไม่ใช่ Apostille ต้องทำอย่างไร?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              ต้องทำ 4 ขั้น: (1) แปลและรับรองนักแปลขึ้นทะเบียน (2) Notary Public (3) รับรองกรมการกงสุล กระทรวงการต่างประเทศไทย (MFA แจ้งวัฒนะ) (4) รับรองโดยสถานทูตของประเทศปลายทางในกรุงเทพฯ. NYC ทำครบในแพ็กเกจเดียว เริ่มต้น 3,000 บาท/ฉบับ จบ 5–14 วันขึ้นกับประเทศ.
            </p>
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> โทร 092-242-9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
            <Link to="/apostille" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <Globe2 className="h-4 w-4" /> ประเทศ Apostille (122 ประเทศ)
            </Link>
            <Link to="/mfa-legalisation/documents" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <Globe2 className="h-4 w-4" /> เอกสารที่ต้องรับรอง 6 กลุ่ม
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">เลือกประเทศปลายทาง</h2>
          <p className="mt-2 text-sm text-muted-foreground">แบ่งตามภูมิภาค · กดเพื่อดูขั้นตอน ราคา ภาษาที่ใช้ และระยะเวลาเฉพาะประเทศ</p>
          <div className="mt-8 space-y-10">
            {Object.entries(byRegion).map(([region, list]) => (
              <div key={region}>
                <h3 className="mb-4 text-lg font-bold text-primary">{region}</h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {list.map((c) => (
                    <Link
                      key={c.slug}
                      to="/mfa-legalisation/$country"
                      params={{ country: c.slug }}
                      className="group rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-0.5 hover:border-primary hover:shadow-md"
                      data-track-zone="mfa-hub-th"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{c.flag}</span>
                        <div>
                          <div className="font-bold">{c.nameTh}</div>
                          <div className="text-xs text-muted-foreground">{c.totalDays} วัน · {c.embassyFeeTHB.toLocaleString()} บาท ค่าสถานทูต</div>
                        </div>
                      </div>
                      <div className="mt-3 line-clamp-2 text-xs text-muted-foreground">{c.topDocs.slice(0, 3).join(" · ")}</div>
                      <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary">
                        ดูขั้นตอน <ArrowRight className="h-3 w-3" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl"><ShieldCheck className="mr-2 inline h-6 w-6 text-primary" />บริการที่เกี่ยวข้อง</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { t: "Apostille (Hague 122 ประเทศ)", d: "MFA อย่างเดียว ไม่ต้องสถานทูต", to: "/apostille" as const },
              { t: "แปลรับรองนักแปลขึ้นทะเบียน", d: "EN/JP/CN/KR/AR/FA และอีก 40 ภาษา", to: "/translate" as const },
              { t: "Notary Public ในวัน", d: "Notarial Services Attorney + Power of Attorney", to: "/notary" as const },
              { t: "Marriage Certificate Translation", d: "สำหรับ Z-Visa, OCI, Iqama, Visa คู่สมรส", to: "/apostille/marriage-certificate" as const },
              { t: "Police Clearance Translation", d: "สำหรับซาอุฯ, คูเวต, UAE, กาตาร์, จีน", to: "/apostille/police-clearance" as const },
              { t: "Work Permit + BOI Visa", d: "สำหรับวิศวกร JP/CN/KR ในไทย", to: "/work-permit" as const },
            ].map((s) => (
              <Link key={s.t} to={s.to} className="rounded-xl border border-border bg-card p-4 hover:border-primary">
                <div className="font-bold">{s.t}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.d}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>
          <QuotableStatsStrip cluster="mfa" locale="th" variant="light" />
          <HubCountryLinks service="mfa-legalisation" lang="th" />
      <ServicePageEnhancer serviceName="MFA + สถานทูต" locale="th" />

      <PlanningToolsLinks locale="th" />
      </main>
  );
}
